// Homepage.js
import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import Text from '../components/atoms/Text';
import { classes } from '../styles/HomepageStyle.js';
import { useLocation } from 'react-router-dom';

function Homepage() {
  const [accountCounts, setAccountCounts] = useState({
    free: 1,
    starter: 1,
    growth: 1,
    agency: 1,
  });

  const location = useLocation();

  // Auto-scroll if URL has a hash
  useEffect(() => {
    if (location.hash === '#pricing') {
      const pricingSection = document.getElementById('pricingSection');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const decrementAccount = (plan) => {
    setAccountCounts((prev) => ({
      ...prev,
      [plan]: Math.max(1, prev[plan] - 1),
    }));
  };

  const incrementAccount = (plan) => {
    setAccountCounts((prev) => ({
      ...prev,
      [plan]: prev[plan] + 1,
    }));
  };

  // Helper component for account count control
  const AccountControl = ({ plan }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
      <Text variant="p" sx={classes.planAccounts}>
        {accountCounts[plan]} Account{accountCounts[plan] > 1 ? 's' : ''}
      </Text>
      <Button
        variant="outlined"
        size="small"
        onClick={() => decrementAccount(plan)}
        sx={{ marginLeft: '10px' }}
      >
        –
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => incrementAccount(plan)}
        sx={{ marginLeft: '5px' }}
      >
        +
      </Button>
    </Box>
  );

  return (
    <Box sx={classes.homepage}>
      {/* HERO SECTION */}
      <Box component="section" sx={classes.heroSection}>
        <Box sx={classes.heroContent}>
          <Box sx={classes.heroImagePlaceholder} />
          <Box sx={classes.heroText}>
            <Text variant="p" sx={classes.heroTag}>
              TWITTER AI AGENT
            </Text>
            <Text variant="h1" sx={classes.heroTitle}>
              Put your Twitter on auto-pilot
            </Text>
            <Text variant="p" sx={classes.heroSubtitle}>
              Sit back while our AI crafts, schedules, and optimizes your
              tweets, growing your audience effortlessly. Set up once and let
              IntenceAI do the heavy lifting.
            </Text>
            <Box component="ul" sx={classes.heroFeaturesList}>
              <Box component="li" sx={classes.heroFeatureItem}>
                <Text variant="p">
                  Auto-posts engaging tweets uniquely tailored to your brand
                </Text>
              </Box>
              <Box component="li" sx={classes.heroFeatureItem}>
                <Text variant="p">
                  Uncovers trending topics and viral insights for optimal timing
                </Text>
              </Box>
              <Box component="li" sx={classes.heroFeatureItem}>
                <Text variant="p">
                  Continuously refines its strategy with real-time analytics
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* PROBLEM SECTION */}
      <Box component="section" sx={classes.problemSection}>
        <Text variant="h2" sx={classes.problemHeading}>
          Social media is hard
        </Text>
        <Text variant="h3" sx={classes.problemSubheading}>
          Tired of juggling multiple accounts and struggling to keep your
          content fresh?
        </Text>
        <Box sx={classes.problemPoints}>
          <Box sx={classes.problemPoint}>
            <Text variant="p">
              Your efforts feel scattered, wasting valuable time and energy.
            </Text>
          </Box>
          <Box sx={classes.problemPoint}>
            <Text variant="p">
              You’re unsure what resonates, leaving growth opportunities
              untapped.
            </Text>
          </Box>
          <Box sx={classes.problemPoint}>
            <Text variant="p">
              Consistently creating engaging content is overwhelming and
              repetitive.
            </Text>
          </Box>
        </Box>
      </Box>

      {/* WORKFLOW SECTION */}
      <Box component="section" sx={classes.workflowSection}>
        <Text variant="h2" sx={classes.workflowHeading}>
          Create a stress-free social media workflow in just five minutes
        </Text>
        <Box sx={classes.workflowContent}>
          <Box sx={classes.workflowImagePlaceholder} />
          <Box sx={classes.workflowDetails}>
            <Text variant="h3" sx={classes.workflowTitle}>
              Organized
            </Text>
            <Text variant="p" sx={classes.workflowText}>
              Manage all your accounts from one intuitive dashboard and
              streamline your social media efforts.
            </Text>
          </Box>
        </Box>
        <Box sx={{ ...classes.workflowContent, ...classes.reverse }}>
          <Box sx={classes.workflowDetails}>
            <Text variant="h3" sx={classes.workflowTitle}>
              Smart
            </Text>
            <Text variant="p" sx={classes.workflowText}>
              Leverage real-time insights and A/B testing to know exactly what
              drives engagement.
            </Text>
          </Box>
          <Box sx={classes.workflowImagePlaceholder} />
        </Box>
        <Box sx={classes.workflowContent}>
          <Box sx={classes.workflowImagePlaceholder} />
          <Box sx={classes.workflowDetails}>
            <Text variant="h3" sx={classes.workflowTitle}>
              Effortless
            </Text>
            <Text variant="p" sx={classes.workflowText}>
              Enjoy a constant stream of on-brand, AI-generated posts that save
              you hours every week.
            </Text>
          </Box>
        </Box>
      </Box>

      {/* AI CLAIM SECTION */}
      <Box component="section" sx={classes.aiClaimSection}>
        <Text variant="p" sx={classes.aiClaim}>
          We’re the first social media AI agent that learns and evolves with
          every post.
        </Text>
      </Box>

      {/* TESTIMONIALS SECTION */}
      <Box component="section" sx={classes.testimonialsSection}>
        <Text variant="h2" sx={classes.testimonialsHeading}>
          Loved by Social Media Managers & Brand Builders Everywhere
        </Text>
        <Box sx={classes.testimonialCards}>
          <Box sx={classes.testimonialCard}>
            <Text variant="p" sx={classes.testimonialName}>
              Sarah J.
              <br />
              Freelance Social Media Manager
            </Text>
            <Box
              component="blockquote"
              sx={{ fontStyle: 'italic', color: '#555' }}
            >
              <Text variant="p">
                IntenceAI saves me hours every week! Managing multiple clients
                has never been easier—the AI-generated content is spot on and my
                clients couldn’t be happier.”
              </Text>
            </Box>
          </Box>
          <Box sx={classes.testimonialCard}>
            <Text variant="p" sx={classes.testimonialName}>
              Alex T.
              <br />
              Content Creator
            </Text>
            <Box
              component="blockquote"
              sx={{ fontStyle: 'italic', color: '#555' }}
            >
              <Text variant="p">
                “My social media runs on auto-pilot now. IntenceAI's content is
                always fresh, relevant, and engaging—I can’t imagine going back
                to manual posting!”
              </Text>
            </Box>
          </Box>
          <Box sx={classes.testimonialCard}>
            <Text variant="p" sx={classes.testimonialName}>
              Mike R.
              <br />
              Small Business Owner
            </Text>
            <Box
              component="blockquote"
              sx={{ fontStyle: 'italic', color: '#555' }}
            >
              <Text variant="p">
                “I finally have control over my content strategy. IntenceAI
                gives me clear insights and a steady stream of AI-generated
                posts that drive real results.”
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* PRICING SECTION */}
      <Box component="section" id="pricingSection" sx={classes.pricingSection}>
        <Text variant="h2" sx={classes.pricingHeading}>
          Pricing
        </Text>
        <Box sx={classes.pricingCards}>
          {/* Free Plan */}
          <Box sx={classes.pricingCard}>
            <Text variant="h3" sx={classes.planName}>
              Free
            </Text>
            <Text variant="p" sx={classes.planPrice}>
              $0 / month
            </Text>
            <AccountControl plan="free" />
            <Box component="ul" sx={classes.planFeatures}>
              <li>Generate up to 3 smart posts per week</li>
              <li>Seamless auto-scheduling</li>
              <li>7-day preview before posts go live</li>
              <li>Basic AI (no advanced self-learning)</li>
            </Box>
            <Button variant="contained" sx={{ fontSize: '0.9rem' }}>
              Get Started
            </Button>
          </Box>

          {/* Starter Plan */}
          <Box sx={classes.pricingCard}>
            <Text variant="h3" sx={classes.planName}>
              Starter
            </Text>
            <Text variant="p" sx={classes.planPrice}>
              $39 / month
            </Text>
            <AccountControl plan="starter" />
            <Box component="ul" sx={classes.planFeatures}>
              <li>Generate up to 5 custom posts daily, per account</li>
              <li>Weekly A/B testing for optimal content</li>
              <li>30-day analytics & insights on peak posting times</li>
              <li>Responsive email support</li>
            </Box>
            <Button variant="contained" sx={{ fontSize: '0.9rem' }}>
              Get Started
            </Button>
          </Box>

          {/* Growth Plan */}
          <Box sx={classes.pricingCard}>
            <Text variant="h3" sx={classes.planName}>
              Growth
            </Text>
            <Text variant="p" sx={classes.planPrice}>
              $149 / month
            </Text>
            <AccountControl plan="growth" />
            <Box component="ul" sx={classes.planFeatures}>
              <li>Generate up to 15 dynamic posts daily, per account</li>
              <li>Comprehensive A/B testing & auto-optimization</li>
              <li>90-day advanced analytics to drive growth</li>
              <li>Collaboration for 2–3 team members</li>
              <li>Priority live chat support</li>
            </Box>
            <Button variant="contained" sx={{ fontSize: '0.9rem' }}>
              Get Started
            </Button>
          </Box>

          {/* Agency Plan */}
          <Box sx={classes.pricingCard}>
            <Text variant="h3" sx={classes.planName}>
              Agency
            </Text>
            <Text variant="p" sx={classes.planPrice}>
              Starting at $449 / month
            </Text>
            <AccountControl plan="agency" />
            <Box component="ul" sx={classes.planFeatures}>
              <li>
                Generate up to 100 high-performance posts daily, per account
              </li>
              <li>Advanced A/B testing & detailed performance insights</li>
              <li>White-label reporting & PDF exports</li>
              <li>Unlimited team roles and streamlined approvals</li>
              <li>Dedicated account manager for personalized support</li>
            </Box>
            <Button variant="contained" sx={{ fontSize: '0.9rem' }}>
              Contact Us
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Homepage;
